import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  CarParkingPlan, CarParkPlanBooking, requestBookCarParkingSpace, requestCancelCarParkBooking,
  requestCarParkingPlan,
  requestCarParkPlanBookingsInCarPark
} from '@features/whereabouts/services/CarParkingPlanService';
import { AppState } from '@app/store/state/app.state';
import { Period } from '@features/whereabouts/models/movements.models';


export interface CarParkingMapViewState {
  carParkDetails: CarParkingPlan[]
  selectedParkingPlanId?: number;
  bookings: CarParkPlanBooking[]
  loading: boolean;
}

export const initialCarParkingMapViewState: CarParkingMapViewState = {
  carParkDetails: [],
  bookings: [],
  loading: false,
};

export const getCarParkDetails: any = createAsyncThunk(
  'carParkingMapView/getCarParkDetails',
  async (params: {officeId: number}, thunkAPI) => {
    const carParkDetails = await requestCarParkingPlan(params.officeId);
    return {
      carParkDetails: carParkDetails,
    }
  }
);

export const getCarParkBookings: any = createAsyncThunk(
  'carParkingMapView/getCarParkBookings',
  async (params: {carParkPlanId: number, dateString: string}, thunkAPI) => {
    const bookings = await requestCarParkPlanBookingsInCarPark(params.carParkPlanId, params.dateString);
    return {
      bookings: bookings,
    }
  }
);

export const bookCarParkSpace: any = createAsyncThunk(
  'carParkingMapView/bookCarParkSpace',
  async (params: {period: Period, userId: string, carParkPlanId: number, carParkingSpaceId: number, dateString: string}, thunkAPI) => {
    await requestBookCarParkingSpace(params.userId, params.dateString, params.carParkingSpaceId, params.period);
    thunkAPI.dispatch(getCarParkBookings({carParkPlanId: params.carParkPlanId, dateString: params.dateString}));
  }
);

export const cancelCarParkBooking: any = createAsyncThunk(
  'carParkingMapView/cancelCarParkBooking',
  async (params: {userId: string, dateString: string, carParkingSpaceId: number, carParkPlanId: number}, thunkAPI) => {
    await requestCancelCarParkBooking(params.userId, params.dateString, params.carParkingSpaceId);
    thunkAPI.dispatch(getCarParkBookings({carParkPlanId: params.carParkPlanId, dateString: params.dateString}));
  }
);


const carParkingMapViewSlice = createSlice({
  name: 'carParkingMapView',
  initialState: initialCarParkingMapViewState,
  reducers: {
    resetCarParkingMapView: (state) => (initialCarParkingMapViewState),
    setSelectedParkingPlanId: (state, action) => ({...state, selectedParkingPlanId: action.payload}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCarParkDetails.pending, (state) => ({...state, carParkDetails: [], loading: true}))
      .addCase(getCarParkDetails.rejected, (state) => ({...state, carParkDetails: [], loading: false}))
      .addCase(getCarParkDetails.fulfilled, (state, action) => ({...state, carParkDetails: action.payload.carParkDetails, loading: false}))
      .addCase(getCarParkBookings.pending, (state) => ({...state, bookings: [], loading: true}))
      .addCase(getCarParkBookings.rejected, (state) => ({...state, bookings: [], loading: false}))
      .addCase(getCarParkBookings.fulfilled, (state, action) => ({...state, bookings: action.payload.bookings, loading: false}))
      .addCase(bookCarParkSpace.pending, (state) => ({...state, loading: true}))
      .addCase(bookCarParkSpace.rejected, (state) => ({...state, loading: false}))
      .addCase(bookCarParkSpace.fulfilled, (state, action) => ({...state, loading: false}))
      .addCase(cancelCarParkBooking.pending, (state) => ({...state, loading: true}))
      .addCase(cancelCarParkBooking.rejected, (state) => ({...state, loading: false}))
      .addCase(cancelCarParkBooking.fulfilled, (state, action) => ({...state, loading: false}));
  },
});



export default carParkingMapViewSlice.reducer;
export const { setSelectedParkingPlanId } = carParkingMapViewSlice.actions;


// Selectors
export const selectCarParkingDetails = (state: AppState) => state.carParkingMapView.carParkDetails;
export const selectCarParkingPlanBookings = (state: AppState) => state.carParkingMapView.bookings;
export const selectSelectedParkingPlanId = (state: AppState) => state.carParkingMapView.selectedParkingPlanId;
export const selectIsLoading = (state: AppState) => state.carParkingMapView.loading;
